<template>
  <div
    ref="playerContainer"
    class="video-player-wrapper"
    :class="wrapperClasses"
    :lang="locale"
    :data-vp-theme="playerTheme ? effectiveBaseTheme : theme"
    :data-vp-uid="effectiveConfig.uid"
    :style="combinedStyle"
    tabindex="0"
    role="region"
    :aria-label="currentTitle || t('player.accessibility.videoPlayer')"
    @mouseenter="handleMouseEnter"
    @mousemove="handleMouseMove"
    @mouseleave="handleMouseLeave"
  >
    <div class="video-player-main">
      <div
        class="video-player"
        :class="{
          'panel-open': player.isPanelOpen.value,
        }"
      >
        <div
          class="video-content"
          :class="{ 'controls-visible': player.showControlBar.value }"
          @click="handleVideoClick"
          @dblclick="handleVideoDoubleClick"
        >
          <!-- Native loop on a single video only: on a playlist it would swallow
               'ended', and the navigation's own loop could never advance. -->
          <video
            ref="videoElement"
            :controls="false"
            :autoplay="false"
            :loop="effectiveConfig.loop && player.totalItems.value <= 1"
            :muted="effectiveConfig.muted ?? effectiveConfig.autoPlay"
            preload="none"
            playsinline
            webkit-playsinline
            :aria-label="currentTitle || t('player.accessibility.videoPlayer')"
          />

          <!-- Ad Layer: mounted only when ads are configured, and mounted ahead of the first ad
               because playNextAd() reads the ad video element in the same tick it shows it. -->
          <AdsPlayer
            v-if="adsManager.hasAds.value"
            v-show="adsManager.isAdPlaying.value"
            :ads-manager="adsManager"
          />
          <AdsOverlay
            v-if="adsManager.isAdPlaying.value"
            :ads-manager="adsManager"
          />
          <AdsControls
            v-if="adsManager.isAdPlaying.value"
            :ads-manager="adsManager"
            :is-muted="player.isMuted.value"
            @toggle-mute="player.toggleMute"
          />

          <!-- Home Page Overlay -->
          <HomePage
            v-if="player.showHomePage.value"
            :visible="player.showHomePage.value"
            :poster-url="posterUrl"
            :poster-fit="effectiveConfig.posterFit"
            :title="currentTitle"
            :duration="currentAsset?.duration"
            :show-info="effectiveConfig.showHomepageInfo !== false"
            :show-play-button="effectiveConfig.showHomepagePlayButton !== false"
            :is-playlist="player.totalItems.value > 1"
            :total-items="player.totalItems.value"
            @play="player.startPlayback"
          />

          <!-- Control Bar -->
          <ControlBar
            v-if="showControls && !player.showHomePage.value && !adsManager.isAdPlaying.value"
            :visible="player.showControlBar.value"
            :player="player"
            :collection-display-mode="displayMode"
            :enable-pip="effectiveConfig.pip !== false"
            :enable-fullscreen="effectiveConfig.enableFullscreen !== false"
            :show-chapters="effectiveConfig.showChapters !== false"
            :overlays="overlays"
            :logo-url="resolvedLogoPosition === 'control_bar' ? resolvedLogoUrl : undefined"
            :logo-click-url="logoClickUrl ?? playerTheme?.logoClickUrl"
            @toggle-pip="player.togglePip"
          />

          <!-- Loading Spinner -->
          <LoadingSpinner
            :visible="(player.isBuffering.value || player.isLoading.value) && !player.showHomePage.value"
          />

          <!-- Action Indicator -->
          <ActionIndicator ref="actionIndicatorRef" />

          <!-- Custom subtitle overlay (cross-browser positioning) -->
          <SubtitleOverlay :cue-html="activeCueHtml" />

          <!-- Subtitle Notification -->
          <SubtitleNotification
            ref="subtitleNotificationRef"
            @open-settings="player.openPanel(PanelType.SETTINGS, { highlight: 'subtitles' })"
          />

          <!-- Top Right Controls -->
          <TopRightControls
            v-if="showControls && !player.showHomePage.value && !adsManager.isAdPlaying.value"
            :visible="player.showControlBar.value"
            :player="player"
            :show-eco-mode="effectiveConfig.showEcoMode !== false"
            :show-info-button="effectiveConfig.showInfoButton !== false"
          />

          <!-- Playlist Indicator (top-left) -->
          <PlaylistIndicator
            v-if="showControls && !player.showHomePage.value && !adsManager.isAdPlaying.value && player.totalItems.value > 1"
            :visible="player.showControlBar.value"
            :current-index="player.currentIndex.value"
            :total-items="player.totalItems.value"
            @toggle="player.togglePanel(PanelType.PLAYLIST)"
          />

          <!-- Muted indicator (shown when started muted via embed param) -->
          <MutedIndicator
            v-if="showControls && !player.showHomePage.value && !adsManager.isAdPlaying.value"
            :visible="player.isMuted.value && !hasUserUnmuted && !mutedIndicatorExpired"
            @unmute="handleUnmute"
          />

          <!-- Resume playback notification -->
          <ResumePlayback
            v-if="!adsManager.isAdPlaying.value"
            :saved-position="playbackResume.savedPosition.value"
            :has-started="player.hasStarted.value"
            @resume="handleResume"
            @dismiss="handleResumeDismiss"
          />

          <!-- Logo overlay -->
          <PlayerLogo
            v-if="resolvedLogoUrl && resolvedLogoPosition !== 'control_bar' && !player.showHomePage.value"
            :logo-url="resolvedLogoUrl"
            :position="resolvedLogoPosition"
            :click-url="logoClickUrl ?? playerTheme?.logoClickUrl"
            :visible="player.showControlBar.value"
          />

          <!-- Interactive overlays (CTA, rating, info, etc.) -->
          <OverlayLayer
            v-if="overlayManager.activeOverlays.value.length > 0"
            :active-overlays="overlayManager.activeOverlays.value"
            @dismiss="overlayManager.dismiss"
            @submit="handleOverlaySubmit"
          />

          <!-- The playlist waits on this entry's end cards: the way on, whenever the viewer wants it -->
          <button
            v-if="player.isHoldingAdvance.value"
            type="button"
            class="next-after-cards"
            @click="player.resumeAdvance()"
          >
            {{ t('player.controls.nextVideo') }}
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
              <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
            </svg>
          </button>

          <!-- Aria Announcer -->
          <AriaAnnouncer ref="ariaAnnouncerRef" />
        </div>

        <!-- Custom CSS injection (staff-only, from admin) — scoped to this player instance -->
        <component :is="'style'" v-if="scopedCustomCss" type="text/css">{{ scopedCustomCss }}</component>

        <!-- Info Panel (drawer) -->
        <InfoPanel v-if="showInfoPanel" :player="player" :show-chapters="effectiveConfig.showChapters !== false" />
      </div>

      <!-- Bottom bar thumbnail strip -->
      <VideoThumbnailStrip
        v-if="isStripMode && displayMode === 'bottom-bar'"
        :items="player.items.value"
        :current-index="player.currentIndex.value"
        :is-playing="player.isPlaying.value"
        direction="horizontal"
        @select="player.goToIndex"
      />
    </div>

    <!-- Sidebar thumbnail strip -->
    <VideoThumbnailStrip
      v-if="isStripMode && displayMode === 'sidebar'"
      :items="player.items.value"
      :current-index="player.currentIndex.value"
      :is-playing="player.isPlaying.value"
      direction="vertical"
      @select="player.goToIndex"
    />
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch, toRef, nextTick, onMounted, onUnmounted, type PropType, type Ref } from 'vue'
  import type { VideoPlayerConfig, VideoPlayerCallbacks, CollectionDisplayMode } from '../types'
  import { PanelType } from '../types'
  import type { VideoPlaybackInfo, VideoAssetListItem } from '../../types/asset'
  import type { OverlayConfig, PlayerTheme } from '../../types/embed'
  import { createUUID } from '../../types/base'
  import type { PlayerError } from '../../types/base'
  import type { CollectionItem } from '../../types/player'
  import { useVideoPlayer } from '../composables/useVideoPlayer'
  import { useKeyboardShortcuts } from '../composables/useKeyboardShortcuts'
  import { useOverlays } from '../composables/useOverlays'
  import { useAriaAnnounce } from '../composables/useAriaAnnounce'
  import { useControlBarVisibility } from '../composables/useControlBarVisibility'
  import { useEffectiveDisplayMode } from '../composables/useEffectiveDisplayMode'
  import { useServerResume } from '../../common/composables/useServerResume'
  import { usePlaybackHeartbeat } from '../../common/composables/usePlaybackHeartbeat'
  import { useVideoI18n } from '../i18n'
  import { usePlayerTheme } from '../../common/composables/usePlayerTheme'
  import { useSubtitleResize } from '../composables/useSubtitleResize'
  import { useSubtitleCues } from '../composables/useSubtitleCues'
  import { useAdsManager } from '../composables/useAdsManager'
  import PlayerLogo from '../../common/components/PlayerLogo.vue'
  import AdsPlayer from './ads/AdsPlayer.vue'
  import AdsControls from './ads/AdsControls.vue'
  import AdsOverlay from './ads/AdsOverlay.vue'
  import HomePage from './overlays/HomePage.vue'
  import OverlayLayer from './overlays/OverlayLayer.vue'
  import ControlBar from './controls/ControlBar.vue'
  import LoadingSpinner from './overlays/LoadingSpinner.vue'
  import ActionIndicator from './overlays/ActionIndicator.vue'
  import SubtitleNotification from './overlays/SubtitleNotification.vue'
  import SubtitleOverlay from './overlays/SubtitleOverlay.vue'
  import TopRightControls from './overlays/TopRightControls.vue'
  import PlaylistIndicator from './overlays/PlaylistIndicator.vue'
  import InfoPanel from './panels/InfoPanel.vue'
  import MutedIndicator from './overlays/MutedIndicator.vue'
  import ResumePlayback from './overlays/ResumePlayback.vue'
  import AriaAnnouncer from '../../common/components/AriaAnnouncer.vue'
  import VideoThumbnailStrip from './collection/VideoThumbnailStrip.vue'

  const props = defineProps({
    /**
     * Full player config with items / PlaybackInfo wiring (asset mode).
     * Optional: pass `src` instead for simple direct playback.
     */
    config: {
      type: Object as PropType<VideoPlayerConfig>,
      default: undefined,
    },
    /**
     * Direct media URL (escape hatch for raw mp4/webm/… or a simple .m3u8).
     * When set, bypasses the PlaybackInfo system. Features that depend on
     * the HLS manifest (quality, subtitles, audio tracks, chapters,
     * downloads) are unavailable in this mode.
     */
    src: {
      type: String,
      default: undefined,
    },
    playbackInfo: {
      type: Object as PropType<VideoPlaybackInfo>,
      default: undefined,
    },
    poster: {
      type: String,
      default: undefined,
    },
    theme: {
      type: String as PropType<'dark' | 'light'>,
      default: 'dark',
    },
    playerTheme: {
      type: Object as PropType<PlayerTheme>,
      default: undefined,
    },
    showControls: {
      type: Boolean,
      default: true,
    },
    pinControls: {
      type: Boolean,
      default: false,
    },
    showPlaylist: {
      type: Boolean,
      default: true,
    },
    collectionDisplayMode: {
      type: String as PropType<CollectionDisplayMode>,
      default: 'drawer',
    },
    overlays: {
      type: Array as PropType<OverlayConfig[]>,
      default: () => [],
    },
    /** Master switch for the branding logo. When false, no logo is shown
     * regardless of the player-theme fallback. */
    showLogo: {
      type: Boolean,
      default: true,
    },
    logoUrl: {
      type: String,
      default: undefined,
    },
    logoPosition: {
      type: String as PropType<'top_left' | 'top_right' | 'bottom_left' | 'bottom_right' | 'control_bar'>,
      default: undefined,
    },
    logoClickUrl: {
      type: String,
      default: undefined,
    },
    /**
     * Whether the viewer may move the playhead — the publication's `seeking`. `false` draws the bar
     * still and refuses every move the viewer makes; the page that framed the player keeps its
     * `seek` command, and the resume prompt still lands where this viewer stopped.
     */
    seeking: {
      type: Boolean,
      default: true,
    },
  })

  const emit = defineEmits<{
    itemChange: [index: number, item: CollectionItem<VideoAssetListItem>]
    play: []
    pause: []
    ended: []
    timeUpdate: [currentTime: number]
    durationChange: [duration: number]
    volumeChange: [volume: number, muted: boolean]
    qualityChange: [quality: string | null]
    fullscreenChange: [isFullscreen: boolean]
    playbackInfoNeeded: [assetId: string]
    error: [error: PlayerError]
    overlaySubmit: [uid: string, data: unknown]
  }>()

  // Refs
  const playerContainer = ref<HTMLElement | null>(null)
  // The mode drawn, which a narrow player turns into the drawer whatever was chosen.
  const displayMode = useEffectiveDisplayMode(() => props.collectionDisplayMode, playerContainer)
  const videoElement = ref<HTMLVideoElement | null>(null)
  const actionIndicatorRef = ref<InstanceType<typeof ActionIndicator> | null>(null)
  const subtitleNotificationRef = ref<InstanceType<typeof SubtitleNotification> | null>(null)
  const ariaAnnouncerRef = ref<InstanceType<typeof AriaAnnouncer> | null>(null)

  // Build an effective config: merge `src` into the provided config, or
  // synthesize a minimal one when only `src` is given.
  const effectiveConfig = computed<VideoPlayerConfig>(() => {
    const base = props.config
    if (base) {
      return props.src ? { ...base, src: props.src } : base
    }
    return {
      uid: createUUID('direct'),
      items: [],
      src: props.src,
      muted: false,
    }
  })

  // Initialize player
  const player = useVideoPlayer({
    holdAtEnd: () => hasPendingEndCards(),
    canSeek: () => props.seeking,
    config: effectiveConfig.value,
    initialPlaybackInfo: props.playbackInfo,
    poster: props.poster,
    callbacks: {
      onItemChange: (index, item) => {
        emit('itemChange', index, item)
      },
      onPlay: () => emit('play'),
      onPause: () => emit('pause'),
      onEnded: () => emit('ended'),
      onTimeUpdate: (time) => emit('timeUpdate', time),
      onDurationChange: (dur) => emit('durationChange', dur),
      onVolumeChange: (vol, muted) => emit('volumeChange', vol, muted),
      onFullscreenChange: (fs) => emit('fullscreenChange', fs),
      onError: (err) => emit('error', err),
    },
  })

  const { t, locale } = useVideoI18n()

  // Ads manager
  const adsManager = useAdsManager({
    onPauseContent: () => player.pause(),
    onResumeContent: () => player.play(),
    currentTime: player.currentTime,
    isPlaying: player.isPlaying,
    isEnded: computed(() => player.playState.value === 'ended'),
    volume: player.volume,
    isMuted: player.isMuted,
    isFullscreen: player.isFullscreen,
    adsConfig: computed(() => effectiveConfig.value.adsConfig),
  })

  // Muted indicator — tracks whether user has manually unmuted
  const hasUserUnmuted = ref(false)
  const mutedIndicatorExpired = ref(false)
  let mutedIndicatorTimer: ReturnType<typeof setTimeout> | null = null

  function handleUnmute(): void {
    player.setMuted(false)
    hasUserUnmuted.value = true
  }

  // Hide indicator if user unmutes via control bar or keyboard
  watch(player.isMuted, (muted) => {
    if (!muted) hasUserUnmuted.value = true
  })

  // Auto-hide muted indicator after 5 seconds once playback starts
  watch(player.hasStarted, (started) => {
    if (started && effectiveConfig.value.muted && !mutedIndicatorTimer) {
      mutedIndicatorTimer = setTimeout(() => {
        mutedIndicatorExpired.value = true
      }, 5000)
    }
  })

  // Resume point — resolved server-side (per viewer) and delivered in the
  // playback info, replacing the old localStorage resume. Drives the
  // "Resume playback?" prompt.
  const playbackResume = useServerResume({
    resumePositionSeconds: computed(() => props.playbackInfo?.resumePositionSeconds ?? null),
    duration: player.duration,
  })

  // Unified playback heartbeat — analytics (session) + resume store, for every
  // viewer (anonymous or authenticated). Active whenever a playback token is
  // present; the analytics sink is skipped server-side when there is no session.
  usePlaybackHeartbeat({
    playbackToken: computed(() => effectiveConfig.value.playbackToken),
    assetId: computed(() =>
      effectiveConfig.value.src ? undefined : player.currentAsset.value?.uid,
    ),
    sessionId: computed(() => props.playbackInfo?.sessionId ?? undefined),
    currentTime: player.currentTime,
    duration: player.duration,
    hasStarted: player.hasStarted,
    playState: player.playState,
  })

  // The player's own resume point, which `seeking` does not govern: it lands where this viewer
  // stopped, a point they were allowed to reach.
  function handleResume(position: number): void {
    playbackResume.clearSavedPosition()
    player.seekTo(position)
  }

  function handleResumeDismiss(): void {
    playbackResume.clearSavedPosition()
  }

  // Overlay manager
  const overlayManager = useOverlays({
    overlays: toRef(props, 'overlays'),
    currentTime: player.currentTime,
    duration: player.duration,
    playState: player.playState,
    showHomePage: player.showHomePage,
    onPauseRequested: () => player.pause(),
    onResumeRequested: () => player.play(),
  })

  // **Each entry of a playlist starts with its cards down and none closed.** What was closed or is
  // still up belongs to the video it fired on: carried over, a card closed on the first entry would
  // never come back, and one left open would sit over the next. A `showOnce` card already shown
  // stays shown — once is once per sitting.
  watch(player.currentIndex, () => overlayManager.resetForEntry())

  /**
   * Whether the entry that ends has a card at its end that will still come up — not closed, and not
   * a `showOnce` card already shown. **Only then does the playlist hold**: otherwise the next entry
   * starts the instant this one ends, and no card "at the end" is ever seen but the last's; and a
   * hold on a card that cannot come up would wait on nothing.
   */
  function hasPendingEndCards(): boolean {
    return props.overlays.some(
      (card) =>
        card.triggerType === 'video_end' && card.overlayType !== 'recommendations' && overlayManager.mayShow(card),
    )
  }

  // **The hold lifts when the end cards are gone** — closed, answered (a submitted card closes
  // itself), or hidden — or when the viewer asks for the next entry.
  const endCardsUp = computed(() =>
    overlayManager.activeOverlays.value.some((active) => active.config.triggerType === 'video_end'),
  )
  watch(endCardsUp, (up, wasUp) => {
    if (wasUp && !up && player.isHoldingAdvance.value) player.resumeAdvance()
  })
  // The end that holds is also what raises the cards, in the same flush: once it is over, a hold
  // with none up — the player's home page covers the video, say — has nothing to wait on.
  watch(player.isHoldingAdvance, (holding) => {
    if (!holding) return
    void nextTick(() => {
      if (player.isHoldingAdvance.value && !endCardsUp.value) player.resumeAdvance()
    })
  })

  function handleOverlaySubmit(uid: string, data: unknown) {
    console.log('[VideoPlayer] handleOverlaySubmit:', uid, data)
    emit('overlaySubmit', uid, data)
    // Recommendations actions (replay/play) handle their own cleanup via dismissAllOverlays
    const isRecommendationAction = data && typeof data === 'object' && 'action' in data
    if (!isRecommendationAction) {
      setTimeout(() => overlayManager.dismiss(uid), 1200)
    }
  }

  // Keyboard shortcuts
  useKeyboardShortcuts({
    containerRef: playerContainer,
    togglePlay: player.togglePlay,
    seek: player.seek,
    canSeek: player.canSeek,
    setVolume: player.setVolume,
    toggleMute: player.toggleMute,
    toggleFullscreen: player.toggleFullscreen,
    toggleSubtitles: player.toggleSubtitles,
    setPlaybackSpeed: player.setPlaybackSpeed,
    next: player.next,
    prev: player.prev,
    closePanel: player.closePanel,
    exitFullscreen: player.toggleFullscreen,
    onAction: (type: string, text?: string) => {
      actionIndicatorRef.value?.show(type, undefined, text)
    },
    skipAd: () => adsManager.skipAd(),
    isAdSkippable: adsManager.isAdSkippable,
    isPlaying: player.isPlaying,
    volume: player.volume,
    currentTime: player.currentTime,
    duration: player.duration,
    playbackSpeed: player.playbackSpeed,
    isFullscreen: player.isFullscreen,
    isPanelOpen: player.isPanelOpen,
    hasSubtitles: computed(() => player.availableSubtitleTracks.value.length > 0),
    hasNextVideo: player.canGoNext,
    hasPreviousVideo: player.canGoPrev,
    disabled: adsManager.isAdPlaying,
  })

  // ARIA announcements
  const { announce } = useAriaAnnounce()

  watch(player.isPlaying, (playing) => {
    announce(playing ? t('player.controls.play') : t('player.controls.pause'))
  })

  watch([player.volume, player.isMuted], ([vol, muted]) => {
    if (muted) {
      announce(t('player.controls.mute'))
    } else {
      announce(`${t('player.controls.volume')} ${Math.round(vol * 100)}%`)
    }
  })

  watch(player.error, (err) => {
    if (err) {
      announce(t('player.errors.generic'), 'assertive')
    }
  })

  // Ad ARIA announcements
  watch(adsManager.isAdPlaying, (playing) => {
    if (playing) announce(t('player.ads.adPlaying'))
  })
  watch(adsManager.isAdSkippable, (skippable) => {
    if (skippable) announce(t('player.ads.adSkippableNow'))
  })

  // Trigger pre-roll on first playback
  watch(player.hasStarted, (started) => {
    if (started && adsManager.hasAds.value) {
      adsManager.initializeAds()
    }
  }, { once: true })

  watch(player.subtitlesEnabled, (enabled) => {
    if (enabled) {
      const tracks = player.availableSubtitleTracks.value
      const idx = player.currentSubtitleTrackIndex.value
      const track = tracks.find((t) => t.index === idx)
      const trackName = track?.name || track?.language || t('player.controls.subtitles')
      // A focused notification announces itself, so announcing here too would
      // read the track name twice.
      const notificationTookFocus = player.activePanel.value !== PanelType.SETTINGS
        && subtitleNotificationRef.value?.show(trackName) === true
      if (!notificationTookFocus) {
        announce(`${t('player.controls.subtitles')}: ${trackName}`)
      }
    } else {
      // A persistent notification would otherwise go on naming a track that is off.
      subtitleNotificationRef.value?.hide()
      announce(`${t('player.controls.subtitles')}: ${t('player.panels.subtitles.off')}`)
    }
  })

  // Control bar visibility
  const pinControlsRef = computed(() => props.pinControls)
  const controlBarVisibility = useControlBarVisibility(
    player.isPlaying,
    playerContainer as Ref<HTMLElement | null>,
    pinControlsRef,
    player.isPanelOpen,
  )

  watch(controlBarVisibility.showControlBar, (visible) => {
    player.showControlBar.value = visible
  })

  // Player theme
  const playerThemeRef = toRef(props, 'playerTheme')
  const { themeStyle, effectiveBaseTheme, customCss } = usePlayerTheme(playerThemeRef)

  const resolvedLogoUrl = computed(() =>
    props.showLogo === false ? undefined : (props.logoUrl ?? props.playerTheme?.logoUrl),
  )
  const resolvedLogoPosition = computed(() => props.logoPosition ?? props.playerTheme?.logoPosition)

  // Subtitle font size relative to container, modulated by the viewer's
  // chosen size preference.
  const { subtitleFontSizeStyle } = useSubtitleResize({
    containerRef: playerContainer,
    isFullscreen: player.isFullscreen,
    userScale: player.subtitleScale,
  })

  // Custom subtitle overlay (cross-browser — Firefox doesn't support WebKit pseudo-elements)
  const { activeCueHtml } = useSubtitleCues(videoElement, player.subtitlesEnabled)

  const combinedStyle = computed(() => ({
    ...themeStyle.value,
    ...subtitleFontSizeStyle.value,
  }))

  const scopedCustomCss = computed(() => {
    if (!customCss.value) return undefined
    // Extract @font-face rules (top-level at-rules that cannot be scoped)
    const fontFaceRegex = /@font-face\s*\{[^}]*\}/g
    const fontFaces = customCss.value.match(fontFaceRegex) || []
    const remainingCss = customCss.value.replace(fontFaceRegex, '').trim()
    const scoped = remainingCss ? `[data-vp-uid="${effectiveConfig.value.uid}"] { ${remainingCss} }` : ''
    return [fontFaces.join('\n'), scoped].filter(Boolean).join('\n')
  })

  // Computed
  const currentTitle = computed(() => {
    const item = player.currentItem.value
    return item?.customTitle || item?.asset.name || ''
  })

  const currentAsset = computed(() => player.currentAsset.value)

  const posterUrl = computed(() => {
    return props.poster || currentAsset.value?.thumbnailUrl
  })

  // Collection display mode
  const isStripMode = computed(
    () => props.showPlaylist && player.totalItems.value > 1 && displayMode.value !== 'drawer',
  )

  const showInfoPanel = computed(() => {
    if (!player.isPanelOpen.value) return false
    if (isStripMode.value && player.activePanel.value === PanelType.PLAYLIST) return false
    return true
  })

  const wrapperClasses = computed(() => ({
    'panel-open': player.isPanelOpen.value,
    // The chosen mode and not the drawn one: these classes also size the frame (bottom-bar's is
    // what gives a player with no parent height its 16:9), which must not collapse on a fallback.
    'collection-mode-bottom-bar': props.collectionDisplayMode === 'bottom-bar',
    'collection-mode-sidebar': props.collectionDisplayMode === 'sidebar',
    'collection-mode-drawer': props.collectionDisplayMode === 'drawer',
  }))

  // Event handlers
  function handleVideoClick(event: MouseEvent): void {
    if (adsManager.isAdPlaying.value) return
    if (overlayManager.activeOverlays.value.length > 0) return
    const target = event.target as HTMLElement
    const isVideoArea = target === event.currentTarget || target === videoElement.value

    if (isVideoArea && !player.showHomePage.value) {
      player.togglePlay()
    }
  }

  function handleVideoDoubleClick(event: MouseEvent): void {
    if (adsManager.isAdPlaying.value) return
    if (overlayManager.activeOverlays.value.length > 0) return
    const target = event.target as HTMLElement
    const isVideoArea = target === event.currentTarget || target === videoElement.value

    if (isVideoArea && !player.showHomePage.value) {
      player.toggleFullscreen()
    }
  }

  function handleMouseEnter(): void {
    controlBarVisibility.showTemporarily()
  }

  function handleMouseMove(): void {
    if (!player.showHomePage.value) {
      controlBarVisibility.showTemporarily()
    }
  }

  function handleMouseLeave(): void {
    // Handled by useControlBarVisibility
  }

  // Watch for external playback info
  watch(
    () => props.playbackInfo,
    (newInfo) => {
      if (newInfo) {
        player.setPlaybackInfo(newInfo.uid, newInfo)
      }
    },
  )

  // Connect refs
  watch(videoElement, (el) => {
    player.videoRef.value = el
  })

  watch(playerContainer, (el) => {
    player.containerRef.value = el
  })

  // Initialize
  onMounted(() => {
    player.videoRef.value = videoElement.value
    player.containerRef.value = playerContainer.value
    player.initAutoPlay()
  })

  // Cleanup
  onUnmounted(() => {
    player.destroy()
  })

  // Expose
  defineExpose({
    player,
    play: player.play,
    pause: player.pause,
    togglePlay: player.togglePlay,
    // The page that framed the player owns this one (`docs/plans/embed-sdk.md` ES6): it reaches
    // the playhead whatever `seeking` says.
    seek: player.seekTo,
    startPlayback: player.startPlayback,
    duration: player.duration,
    next: player.next,
    prev: player.prev,
    setVolume: player.setVolume,
    setPlaybackSpeed: player.setPlaybackSpeed,
    goToIndex: player.goToIndex,
    setPlaybackInfo: player.setPlaybackInfo,
    dismissAllOverlays: overlayManager.resetShownState,
  })
</script>

<style lang="scss">
  @import '../styles/main.scss';
</style>
